import React from 'react';
import Icon from '../../../components/AppIcon';

const StatCard = ({ icon, label, primary, secondary, helper, loading }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center space-x-4 shadow-sm">
      <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
        {loading ? (
          <div className="w-6 h-6 rounded-full bg-blue-100 animate-pulse" />
        ) : (
          <Icon name={icon} size={22} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-500 truncate">{label}</p>
        {loading ? (
          <div className="mt-2 space-y-2">
            <div className="h-5 w-24 bg-gray-100 rounded animate-pulse" />
            <div className="h-3 w-32 bg-gray-100 rounded animate-pulse" />
          </div>
        ) : (
          <>
            <p className="text-2xl font-semibold text-gray-900">{primary ?? '-'}</p>
            {secondary && (
              <p className="text-sm text-gray-600">{secondary}</p>
            )}
            {helper && (
              <p className="text-xs text-gray-400 mt-0.5">{helper}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const HRStats = ({ stats, loading, onRefresh }) => {
  const totalEmployees = stats?.totalEmployees ?? 0;
  const attendanceTotal = stats?.attendance?.total ?? 0;
  const attendancePresent = stats?.attendance?.present ?? 0;
  const attendanceAbsent = stats?.attendance?.absent ?? Math.max(attendanceTotal - attendancePresent, 0);
  const pendingPayrolls = stats?.pendingPayrolls ?? 0;
  const activeSalaries = stats?.activeSalaries ?? 0;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">HR Overview</h2>
          <p className="text-sm text-gray-500">Realtime glance at workforce health</p>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <Icon name={loading ? 'Loader2' : 'RefreshCcw'} size={16} className={loading ? 'animate-spin' : ''} />
          <span className="ml-2">Refresh</span>
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon="Users"
          label="Total Employees"
          primary={totalEmployees}
          helper="Active employees in system"
          loading={loading}
        />
        <StatCard
          icon="Clock"
          label="Present Today"
          primary={attendancePresent}
          secondary={`Out of ${attendanceTotal} marked`}
          helper={`${attendanceAbsent} absent today`}
          loading={loading}
        />
        <StatCard
          icon="Wallet"
          label="Pending Payrolls"
          primary={pendingPayrolls}
          helper="Awaiting payment confirmation"
          loading={loading}
        />
        <StatCard
          icon="BadgeDollarSign"
          label="Active Salary Profiles"
          primary={activeSalaries}
          helper="Employees with valid salary structures"
          loading={loading}
        />
      </div>
    </div>
  );
};

export default HRStats;
